import React from 'react';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import { Box } from '@mui/material';
import { C } from './leadOverviewCardTokens';

const formatIntentDisplay = (raw) => {
  const s = String(raw || '').trim().toLowerCase().replace(/-/g, '_');
  if (!s) return '';
  if (s === 'not_sure' || s.startsWith('not_sure_')) return 'Not Sure';
  return s
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');
};

export const temperatureIntentShortWord = (intentRaw) => {
  const s = String(intentRaw || '').toLowerCase().replace(/-/g, '_');
  if (!s.trim()) return '—';
  if (s.includes('hot')) return 'Hot';
  if (s.includes('warm')) return 'Warm';
  if (s.includes('cold')) return 'Cold';
  return formatIntentDisplay(intentRaw) || '—';
};

export const temperatureIntentDisplayLabel = (intentRaw) => {
  const s = String(intentRaw || '').trim().toLowerCase().replace(/-/g, '_');
  if (!s) return '';
  if (s === 'not_sure' || s.includes('not_sure')) return 'Not Sure';
  if (s.includes('hot')) return 'Hot Lead';
  if (s.includes('warm')) return 'Warm Lead';
  if (s.includes('cold')) return 'Cold Lead';
  return formatIntentDisplay(intentRaw) || '';
};

export const isTemperatureNotSureIntent = (intentRaw) => {
  const s = String(intentRaw || '').trim().toLowerCase().replace(/-/g, '_');
  return s === 'not_sure' || s.includes('not_sure');
};

export const temperatureIntentHeroColor = (heroWord, rawIntent = '') => {
  const rNorm = String(rawIntent || '').toLowerCase().replace(/-/g, '_');
  if (rNorm === 'not_sure' || rNorm.includes('not_sure')) return C.slate4;
  const h = String(heroWord || '').trim().toLowerCase();
  if (h === 'not sure') return C.slate4;
  if (h === 'hot') return '#E11D48';
  if (h === 'warm') return '#EA580C';
  if (h === 'cold') return C.blue;
  const r = String(rawIntent || '').toLowerCase();
  if (r.includes('hot')) return '#E11D48';
  if (r.includes('warm')) return '#EA580C';
  if (r.includes('cold')) return C.blue;
  return C.slate2;
};

export const resolveTemperatureIntentTier = (heroWord, rawIntent = '') => {
  const h = String(heroWord || '').trim().toLowerCase();
  if (h === 'hot') return 'hot';
  if (h === 'warm') return 'warm';
  if (h === 'cold') return 'cold';
  const r = String(rawIntent || '').toLowerCase();
  if (r.includes('hot')) return 'hot';
  if (r.includes('warm')) return 'warm';
  if (r.includes('cold')) return 'cold';
  return 'other';
};

export const resolveLeadTemperatureBarFillPct = (lt, heroWord, rawIntent) => {
  const tier = resolveTemperatureIntentTier(heroWord, rawIntent);
  if (tier === 'cold') return 35;
  if (tier === 'warm') return 70;
  if (tier === 'hot') return 100;

  const raw = lt?.bar_fill_percent ?? lt?.bar_fill_pct;
  const n = raw != null && raw !== '' ? Number(raw) : NaN;
  if (!Number.isNaN(n)) {
    return Math.round(Math.max(0, Math.min(100, n)));
  }
  const legacy = lt?.score != null && lt.score !== '' ? Number(lt.score) : NaN;
  if (!Number.isNaN(legacy)) {
    return Math.round(Math.max(0, Math.min(100, legacy)));
  }
  return 50;
};

/** Mirror backend `GeneralHelper.extract_lead_temperature_intent` for offline / stale API fallback. */
export const extractTemperatureIntentFromTags = (tags) => {
  if (!Array.isArray(tags) || !tags.length) return null;
  const toStr = (raw) => {
    if (raw == null) return '';
    if (typeof raw === 'object') {
      return String(raw.name || raw.label || raw.value || '').trim();
    }
    return String(raw).trim();
  };
  const classify = (tagStr) => {
    if (!tagStr) return null;
    const t = tagStr.toLowerCase().replace(/-/g, ' ');
    const u = tagStr.toLowerCase().replace(/-/g, '_');
    if (t.includes('hot lead') || u.includes('hot_lead')) return 'hot_lead';
    if (t.includes('warm lead') || u.includes('warm_lead')) return 'warm_lead';
    if (t.includes('cold lead') || u.includes('cold_lead')) return 'cold_lead';
    if (t === 'hot' || u === 'hot') return 'hot_lead';
    if (t === 'warm' || u === 'warm') return 'warm_lead';
    if (t === 'cold' || u === 'cold') return 'cold_lead';
    return null;
  };
  const order = { hot_lead: 0, warm_lead: 1, cold_lead: 2 };
  const found = [];
  tags.forEach((raw) => {
    const c = classify(toStr(raw));
    if (c) found.push(c);
  });
  if (!found.length) return null;
  return found.reduce((a, b) => (order[a] <= order[b] ? a : b));
};

/** When overview omits intent but lead still has temperature tags on the record. */
export const mergeLeadTemperatureWithTagFallback = (leadTemperature, leadTags) => {
  const base = leadTemperature && typeof leadTemperature === 'object' ? { ...leadTemperature } : {};
  const cur = String(base.current_intent ?? '').trim();
  if (cur) return { ...base, current_intent: cur };

  const fromTags = extractTemperatureIntentFromTags(leadTags);
  if (!fromTags) return leadTemperature ?? null;

  const bar =
    base.bar_fill_percent != null && base.bar_fill_percent !== ''
      ? base.bar_fill_percent
      : resolveLeadTemperatureBarFillPct(
          { ...base, score: null },
          temperatureIntentShortWord(fromTags),
          fromTags,
        );
  return {
    ...base,
    current_intent: fromTags,
    journey: Array.isArray(base.journey) ? base.journey : [],
    bar_fill_percent: bar,
  };
};

export const deriveLeadTemperatureUi = (leadTemperature) => {
  if (leadTemperature == null) return { kind: 'null' };
  const lt = leadTemperature;
  const journeyRaw = Array.isArray(lt.journey) ? lt.journey : [];
  const cur = String(lt.current_intent ?? '').trim();
  const lastJ = journeyRaw.length ? String(journeyRaw[journeyRaw.length - 1]?.intent ?? '').trim() : '';
  const currentKey = cur || lastJ;
  const hasIntent = Boolean(currentKey);
  if (!hasIntent) return { kind: 'no_intent' };
  const intentShort = temperatureIntentShortWord(currentKey);
  const intentLabel = temperatureIntentDisplayLabel(currentKey);
  const notSureTemp = isTemperatureNotSureIntent(currentKey);
  const barFillPct = resolveLeadTemperatureBarFillPct(lt, intentShort, currentKey);
  const intentColor = temperatureIntentHeroColor(intentShort, currentKey);
  return {
    kind: 'intent',
    currentKey,
    intentShort,
    intentLabel,
    notSureTemp,
    barFillPct,
    intentColor,
  };
};

const TEMPERATURE_TIER_ICONS = {
  hot: LocalFireDepartmentIcon,
  warm: WbSunnyIcon,
  cold: AcUnitIcon,
  other: DeviceThermostatIcon,
};

export const TemperatureIntentIcon = ({ heroWord, rawIntent, sx }) => {
  const tier = resolveTemperatureIntentTier(heroWord, rawIntent);
  const IconComponent = TEMPERATURE_TIER_ICONS[tier] || DeviceThermostatIcon;
  const color = temperatureIntentHeroColor(heroWord, rawIntent);
  return <IconComponent sx={{ color, flexShrink: 0, ...sx }} aria-hidden />;
};

const TEMPERATURE_SPECTRUM_GRADIENT =
  'linear-gradient(90deg, #2563EB 0%, #38BDF8 14%, #2DD4BF 30%, #FACC15 48%, #FB923C 66%, #EA580C 82%, #C2410C 100%)';
const TEMPERATURE_TRACK_HEIGHT = 12;
const TEMPERATURE_THUMB_SIZE = 18;

export const TemperatureSpectrumBar = ({ value, thumbColor }) => {
  const pct = Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
  const col = thumbColor || '#EA580C';
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        py: '10px',
      }}
    >
      <Box
        sx={{
          height: TEMPERATURE_TRACK_HEIGHT,
          borderRadius: '999px',
          width: '100%',
          background: TEMPERATURE_SPECTRUM_GRADIENT,
          boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.1)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: `${pct}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: TEMPERATURE_THUMB_SIZE,
          height: TEMPERATURE_THUMB_SIZE,
          borderRadius: '50%',
          bgcolor: col,
          border: '2px solid #FFFFFF',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.06)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};
